import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Certificate, Lock, MapPin, CalendarBlank, Bed, Check, X, FilePdf, Bookmark } from '@phosphor-icons/react';
import axios from 'axios';
import RichContent from '../components/RichContent';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CertificatePage = () => {
    const { folio } = useParams();
    const [verified, setVerified] = useState(false);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [phoneLast4, setPhoneLast4] = useState('');
    const [certData, setCertData] = useState(null);

    const handleVerify = async (e) => {
        e.preventDefault();
        if (!email || !phoneLast4 || phoneLast4.length !== 4) {
            toast.error('Ingresa tu email y los últimos 4 dígitos de tu teléfono');
            return;
        }
        
        setLoading(true);
        try {
            const response = await axios.post(`${API}/public/cert/verify`, {
                folio: folio,
                email: email,
                phone_last4: phoneLast4
            });
            setCertData(response.data);
            setVerified(true);
        } catch (error) {
            const detail = error.response?.data?.detail || 'Error al verificar';
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('es-MX', { 
            timeZone: 'America/Mexico_City',
            year: 'numeric', month: 'long', day: 'numeric' 
        });
    };

    if (!verified) {
        return (
            <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4" data-testid="cert-verify-page">
                <Card className="w-full max-w-md bg-white border-[#ECE3D2] rounded-2xl shadow-lg">
                    <CardContent className="p-8">
                        <div className="text-center mb-6">
                            <svg viewBox="0 0 800 375" className="h-12 mx-auto mb-5" aria-label="Exploremos" xmlns="http://www.w3.org/2000/svg">
                                <path d="M 165 245 Q 400 110 635 245" fill="none" stroke="#FB7185" strokeWidth="10" strokeLinecap="round" />
                                <text x="400" y="340" textAnchor="middle" fontFamily="'Inter','Helvetica Neue',Arial,sans-serif" fontSize="92" fontWeight="700" fill="#0F1B3D" letterSpacing="-2.5">exploremos</text>
                            </svg>
                            <h1 className="font-display text-2xl font-bold text-[#1F2D4B]">
                                Verifica tu certificado
                            </h1>
                            <p className="text-[#4B5878] mt-2 text-sm">
                                Folio <span className="font-mono-brand font-bold text-[#1F2D4B]">{folio}</span>
                            </p>
                            <p className="text-xs text-[#8B8474] mt-1">
                                Ingresa tus datos para verificar tu identidad
                            </p>
                        </div>
                        
                        <form onSubmit={handleVerify} className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-[#1F2D4B] mb-1 block">Email</label>
                                <Input
                                    type="email"
                                    placeholder="tu@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="cert-verify-email"
                                    className="h-12 rounded-xl border-[#ECE3D2] bg-white text-[#1F2D4B]"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-[#1F2D4B] mb-1 block">
                                    Últimos 4 dígitos de tu teléfono
                                </label>
                                <Input
                                    type="text"
                                    placeholder="1234"
                                    maxLength={4}
                                    value={phoneLast4}
                                    onChange={(e) => setPhoneLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                    data-testid="cert-verify-phone"
                                    className="h-12 rounded-xl border-[#ECE3D2] bg-white text-[#1F2D4B] text-center text-2xl tracking-[0.5em] font-mono"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="cert-verify-btn"
                                className="w-full h-12 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl font-medium"
                            >
                                {loading ? 'Verificando...' : 'Ver mi Certificado'}
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const { cancelled, certificate, product, customer, sale } = certData;

    if (cancelled) {
        return (
            <div className="min-h-screen bg-[#FAF6F0] p-4 md:p-8 flex items-center justify-center" data-testid="cert-cancelled-page">
                <div className="max-w-lg w-full">
                    <div className="flex justify-center mb-6">
                        <img src="/brand/exploremos-logo-navy.png" alt="Exploremos" className="h-24 w-auto" />
                    </div>
                    <Card className="border-2 border-rose-200 bg-white">
                        <CardContent className="p-8 text-center">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
                                <span className="text-rose-600 text-3xl font-bold">✕</span>
                            </div>
                            <h1 className="font-display text-2xl font-bold text-[#1F2D4B] mb-2">
                                Certificado cancelado
                            </h1>
                            <p className="text-sm text-[#4B5878] mb-4">
                                Hola {customer?.name || ''}, tu certificado <span className="font-mono font-semibold text-[#1F2D4B]">{certificate.folio}</span> fue cancelado
                                {certificate.cancelled_at ? ` el ${new Date(certificate.cancelled_at).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City', day: 'numeric', month: 'long', year: 'numeric' })}` : ''} y ya no está disponible para uso.
                            </p>
                            {certificate.refund_folio && (
                                <p className="text-xs text-[#8B8474] bg-[#F8F4EC] rounded-md p-3 mt-4">
                                    Folio de devolución: <span className="font-mono font-semibold text-[#1F2D4B]">{certificate.refund_folio}</span>
                                </p>
                            )}
                            <p className="text-xs text-[#8B8474] mt-4">
                                Si tienes preguntas sobre esta cancelación, contáctanos.
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    const handleDownloadPDF = () => {
        const pdfUrl = `${API}/public/cert/${folio}/pdf?email=${encodeURIComponent(email)}&phone_last4=${encodeURIComponent(phoneLast4)}`;
        window.open(pdfUrl, '_blank');
    };

    return (
        <div className="min-h-screen bg-[#FAF6F0] p-4 md:p-8" data-testid="cert-display-page">
            <div className="max-w-2xl mx-auto">
                {/* Brand header */}
                <div className="flex justify-center mb-6">
                    <img 
                        src="/brand/exploremos-logo-navy.png" 
                        alt="Exploremos" 
                        className="h-32 w-auto"
                        data-testid="cert-brand-logo"
                    />
                </div>

                {/* Certificate Card */}
                <Card className="bg-white border-[#ECE3D2] rounded-2xl shadow-lg overflow-hidden">
                    {/* Header Image */}
                    {product?.design?.background_image && (
                        <div 
                            className="h-48 bg-cover bg-center"
                            style={{ backgroundImage: `url(${product.design.background_image})` }}
                        />
                    )}
                    
                    <CardContent className="p-8">
                        {/* Certificate Title */}
                        <div className="text-center mb-6">
                            <Badge className="bg-[#D1FAE5] text-[#059669] border-0 mb-3">
                                Certificado Verificado
                            </Badge>
                            <h1 className="font-editorial text-3xl font-bold text-[#1F2D4B]">
                                {product?.title || product?.name}
                            </h1>
                            {product?.subtitle && (
                                <p className="text-lg text-[#4B5878] mt-1">{product.subtitle}</p>
                            )}
                        </div>

                        {/* Brief description (italic) */}
                        {product?.brief_description && (
                            <div className="text-center text-[#4B5878] italic mb-6 leading-relaxed -mt-2">
                                <RichContent value={product.brief_description} />
                            </div>
                        )}

                        {/* Folio */}
                        <div className="text-center mb-6 p-4 bg-[#F8F4EC] rounded-xl border border-[#ECE3D2]">
                            <p className="text-sm text-[#4B5878] uppercase tracking-wider">Folio</p>
                            <p className="font-mono text-2xl font-bold text-[#FB7185] mt-1">
                                {certificate?.folio}
                            </p>
                        </div>

                        {/* Details */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                                <div className="flex items-center gap-2 text-[#4B5878] mb-1">
                                    <MapPin size={16} weight="duotone" />
                                    <span className="text-xs">Destino</span>
                                </div>
                                <p className="font-medium text-[#1F2D4B] text-sm">{product?.destination}</p>
                            </div>
                            <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                                <div className="flex items-center gap-2 text-[#4B5878] mb-1">
                                    <Bed size={16} weight="duotone" />
                                    <span className="text-xs">Noches</span>
                                </div>
                                <p className="font-medium text-[#1F2D4B] text-sm">{product?.nights} noches</p>
                            </div>
                            <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                                <div className="flex items-center gap-2 text-[#4B5878] mb-1">
                                    <CalendarBlank size={16} weight="duotone" />
                                    <span className="text-xs">Válido hasta</span>
                                </div>
                                <p className="font-medium text-[#1F2D4B] text-sm">{formatDate(certificate?.valid_until)}</p>
                            </div>
                            <div className="p-4 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                                <div className="flex items-center gap-2 text-[#4B5878] mb-1">
                                    <Certificate size={16} weight="duotone" />
                                    <span className="text-xs">A nombre de</span>
                                </div>
                                <p className="font-medium text-[#1F2D4B] text-sm">{customer?.name}</p>
                            </div>
                        </div>

                        {/* Full description */}
                        {(product?.description || product?.full_description) && (
                            <div className="mb-6 pt-5 border-t border-[#ECE3D2]">
                                <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#8B8474] mb-2">
                                    Descripción
                                </h3>
                                <RichContent
                                    value={product?.full_description || product?.description}
                                    className="text-sm text-[#4B5878] leading-relaxed"
                                />
                            </div>
                        )}

                        {/* Includes */}
                        {product?.includes?.length > 0 && (
                            <div className="mb-6 pt-5 border-t border-[#ECE3D2]">
                                <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#059669] mb-3 flex items-center gap-1.5">
                                    <Check size={13} weight="bold" />
                                    Qué incluye
                                </h3>
                                <div className="space-y-2">
                                    {product.includes.map((item, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <Check size={15} weight="bold" className="text-[#059669] mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-[#4B5563] leading-relaxed">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Excludes */}
                        {product?.excludes?.length > 0 && (
                            <div className="mb-6 pt-5 border-t border-[#ECE3D2]">
                                <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#C8413A] mb-3 flex items-center gap-1.5">
                                    <X size={13} weight="bold" />
                                    Qué NO incluye
                                </h3>
                                <div className="space-y-2">
                                    {product.excludes.map((item, i) => (
                                        <div key={i} className="flex items-start gap-2">
                                            <X size={15} weight="bold" className="text-[#C8413A] mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-[#4B5563] leading-relaxed">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* How to Book */}
                        {product?.how_to_book && (
                            <div className="mb-6 p-5 rounded-xl bg-[#FFF8E7] border border-[#F5DEB3]">
                                <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#92400E] mb-2 flex items-center gap-1.5">
                                    <Bookmark size={13} weight="bold" />
                                    Cómo Usar / Reservar
                                </h3>
                                <RichContent
                                    value={product.how_to_book}
                                    className="text-sm text-[#92400E] leading-relaxed"
                                />
                            </div>
                        )}

                        {/* Terms */}
                        {product?.terms_and_conditions && (
                            <div className="pt-5 border-t border-[#ECE3D2]">
                                <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[#8B8474] mb-2">
                                    Términos y Condiciones
                                </h3>
                                <RichContent
                                    value={product.terms_and_conditions}
                                    className="text-xs text-[#4B5878] leading-relaxed"
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Footer */}
                <div className="flex justify-center mt-6">
                    <Button
                        onClick={handleDownloadPDF}
                        data-testid="download-pdf-btn"
                        className="h-12 px-8 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl font-medium"
                    >
                        <FilePdf size={20} className="mr-2" />
                        Descargar PDF
                    </Button>
                </div>
                
                <p className="text-center text-xs text-[#4B5878] mt-4">
                    Certificado emitido por Exploremos | Fecha de compra: {formatDate(sale?.created_at)}
                </p>
            </div>
        </div>
    );
};

export default CertificatePage;
